<template>
  <div class="residential-account-complaint-mutate">
    <Card tag="form" theme="flat" @submit.prevent="submit">
      <template #header>
        <FontAwesomeIcon :icon="['fal', 'file']" />
        <h2 class="text-left">{{ $t('complaintRegister') }}</h2>
      </template>
      <template #default>
        <div data-widget="reference" class="row" v-if="!item.id">
          <SelectComponent
            v-model="project"
            items="/api/resource/project?field.id=id&field.label=name&addon=project:complaint"
            search
            :label="$t('selectx', { x: $tc('project', 1).toLowerCase() })"
          />
          <SelectComponent
            v-if="project"
            v-model="data.reference.ref"
            :items="fetchResidentialAccounts"
            search
            :label="$t('selectx', { x: $tc('unit', 1).toLowerCase() })"
          />
        </div>
        <InputComponent :label="$t('complaintTermsOfRequest')" v-model="data.title" data-widget="terms" required />
        <InputComponent :label="$t('complaintDescription')" data-widget="description" required>
          <textarea
            v-model="data.description"
            rows="3"
            class="elder-input__element"
            style="resize: vertical"
            :placeholder="$t('typeHere') + '...'"
          ></textarea>
        </InputComponent>
        <FileComponent
          data-widget="images"
          :label="$tc('image', 2)"
          accept="image/*"
          multiple
          v-model="data.images"
          :upload="uploadImage"
        >
          <template #action="{ item }">
            <ButtonComponent
              class="residential-account-complaint-mutate__edit-action"
              theme="secondary"
              v-if="item.type.startsWith('image')"
              :label="$t('complaintAddSelection')"
              @click="$emit('edit', item)"
            />
          </template>
        </FileComponent>
        <FileComponent
          data-widget="attachment"
          :label="$tc('attachment', 2)"
          multiple
          v-model="data.attachments"
          :upload="upload"
        />
        <Alert class="residential-account-complaint-mutate__disclaimer-alert"
          ><CheckboxComponent
            v-model="disclaimerConsent"
            required
            :label="$path('metadata.settings.complaint.disclaimer', item) || $t('complaintDisclaimerConsent')"
        /></Alert>
      </template>
      <template #footer>
        <slot name="footer" />
        <ButtonComponent
          type="submit"
          :promise="promise"
          :disabled="!validator.passes || !disclaimerConsent"
          :label="$t('send')"
          theme="primary"
          :icon="['fal', 'paper-plane']"
          @onSuccess="onSuccess"
        />
      </template>
    </Card>
  </div>
</template>

<script>
import Bus from '@/bus'
import API from '@/api/ResidentialAccount'
import ModelMixin from '@/mixins/Model'
import Validator from '@/mixins/validator-v2'
import { VueComponentUpload as upload } from '@kvass/storage'
import { mapActions, mapState } from 'vuex'

import Card from '@/components/Card'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { FileComponent } from '@kvass/file'
import { InputComponent } from 'vue-elder-input'
import SelectComponent from '@kvass/custom-fields/src/Fields/SelectModal'

export default {
  mixins: [
    Validator({
      rules: {
        title: 'required',
        attachments: 'array',
        description: 'required',
        reference: 'required',
      },
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      model: {
        title: null,
        description: null,
        dueAt: null,
        members: [],
        images: [],
        attachments: [],
        type: 'complaint',
        tags: [],
        reference: {
          onModel: 'ResidentialAccount',
          ref: null,
        },
      },
      transform: {
        members(v) {
          return v && v.map(e => e.id)
        },
      },
    }),
  ],
  watch: {
    data: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true,
    },
  },
  data() {
    return {
      promise: null,
      disclaimerConsent: false,
      project: null,
    }
  },
  computed: {
    ...mapState('ResidentialAccount', {
      item: function (state) {
        switch (this.$path('$route.meta.context')) {
          case 'unit':
            return state.items[0]
          default:
            return state.item
        }
      },
    }),
    labels() {
      return {
        name: this.$t('name'),
        email: this.$t('email'),
      }
    },
    rules() {
      return {
        name: 'required',
        email: 'required|email',
        message: 'required|string',
      }
    },
  },
  methods: {
    ...mapActions('Task', ['mutate']),
    submit() {
      if (!this.data.reference.ref) this.data.reference.ref = this.item.id
      if (!this.validator.passes || !this.disclaimerConsent) return
      return (this.promise = this.mutate([
        {
          ...this.getData({ diff: false }),
          status: 'request',
        },
      ]))
    },
    onSuccess() {
      Bus.$emit('tasks:fetch')
      this.$emit('close')
    },
    uploadImage(rawFile, onProgress, options = {}) {
      return upload(rawFile, onProgress, { ...options, compression: 'gallery' })
    },

    fetchResidentialAccounts() {
      return API.get({ filter: { project: this.project } }, { query: 'ResidentialAccountSelect' }).then(res =>
        res.filter(Boolean).map(i => ({ label: i.reference.name, id: i.id })),
      )
    },
    upload,
  },
  created() {
    this.project = this.$path('$route.meta.context') === 'project' ? this.$route.params.id : null
    if (!this.data.members.length && this.$store.state.user)
      this.data.members.push({
        id: this.$store.state.user.id,
        name: this.$store.state.user.name,
        email: this.$store.state.user.email,
        avatar: this.$store.state.user.avatar,
      })
  },

  components: {
    Card,
    InputComponent,
    FileComponent,
    CheckboxComponent,
    Alert,
    SelectComponent,
  },
}
</script>

<style lang="scss">
.residential-account-complaint-mutate {
  max-width: 700px;
  width: 100%;

  &__disclaimer-alert {
    .elder-checkbox__box {
      border-color: css-darken('border-color', 15%);
    }
  }

  &__edit-action {
    font-size: 0.8em;
  }

  .card__header {
    min-height: initial;
    flex-direction: row;
    justify-content: center !important;
    padding: 2rem !important;

    & > svg {
      margin-right: 1.5rem;
    }

    h2 {
      margin: 0;
    }
  }

  .card__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  [data-widget='reference'] {
    display: flex;
    gap: 1rem;
    & > * {
      flex-grow: 1;
    }

    .elder-input__label {
      &::after {
        content: '*';
        color: var(--vue-elder-error, #e83b35);
      }
    }
  }

  [data-widget='images'] {
    .kvass-file__thumbnail {
      align-items: flex-start;
    }

    img {
      height: 75px;
      background-color: var(--border-color);
    }
  }
}
</style>
